import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { Seo } from "@/components/brand/Seo";
import { useAuth, PORTAL_PATH } from "@/lib/AuthContext";
import { apiError } from "@/lib/api";

export function AuthShell({ title, subtitle, children, testId }) {
  return (
    <div className="flex min-h-screen flex-col bg-white lg:flex-row">
      <div className="hidden cream lg:flex lg:w-[42%] lg:flex-col lg:justify-between lg:p-12">
        <Link to="/">
          <Logo height={32} />
        </Link>
        <div>
          <h2 className="max-w-md text-3xl leading-tight">People trust people</h2>
          <p className="mt-5 max-w-sm text-base leading-relaxed body-text">
            One sign-in for AnyTribe clients, creators and the internal team. Your access is set
            by AnyTribe — you'll land on the right portal automatically.
          </p>
        </div>
        <p className="text-sm body-text">
          Need an account?{" "}
          <a href="mailto:hello@anytribemarketing.com" className="font-semibold text-[#F2811D]">
            hello@anytribemarketing.com
          </a>
        </p>
      </div>

      <div className="flex flex-1 items-center justify-center px-5 py-14 lg:px-12">
        <div className="w-full max-w-[420px]" data-testid={testId}>
          <div className="lg:hidden">
            <Link to="/">
              <Logo height={30} />
            </Link>
          </div>
          <h1 className="mt-9 text-3xl lg:mt-0">{title}</h1>
          {subtitle && <p className="mt-3 text-[15px] leading-relaxed body-text">{subtitle}</p>}
          <div className="mt-8">{children}</div>
        </div>
      </div>
    </div>
  );
}

export function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-semibold text-[#111111]">{label}</span>
      {children}
      {hint && <span className="mt-1.5 block text-xs body-text">{hint}</span>}
    </label>
  );
}

export const inputClass =
  "w-full rounded-xl border border-[#E6E1D8] bg-white px-4 py-3 text-[15px] text-[#111111] outline-none transition-colors placeholder:text-[#a8a29a] focus:border-[#F2811D] focus:ring-2 focus:ring-[#F2811D]/15";

export function SubmitButton({ busy, children, testId }) {
  return (
    <button
      type="submit"
      disabled={busy}
      data-testid={testId}
      className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#F2811D] px-6 py-3.5 text-[15px] font-semibold text-white transition-colors hover:bg-[#D96E10] disabled:cursor-not-allowed disabled:opacity-60"
    >
      {busy && <Loader2 className="h-4 w-4 animate-spin" />}
      {children}
    </button>
  );
}

export function FormError({ message }) {
  if (!message) return null;
  return (
    <p
      className="rounded-xl border border-[#F2C2C2] bg-[#FDF3F3] px-4 py-3 text-sm text-[#B3261E]"
      data-testid="form-error"
    >
      {message}
    </p>
  );
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const user = await login(email.trim(), password);
      navigate(PORTAL_PATH[user.role] || "/", { replace: true });
    } catch (e) {
      setError(apiError(e, "We couldn't sign you in. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      title="Sign in"
      subtitle="Use the email address AnyTribe set your account up with."
      testId="login-page"
    >
      <Seo title="Sign in — AnyTribe Marketing" description="Sign in to your AnyTribe portal." />
      <form onSubmit={submit} className="space-y-5" data-testid="login-form">
        <FormError message={error} />
        <Field label="Email address">
          <input
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputClass}
            data-testid="login-email-input"
          />
        </Field>
        <Field label="Password">
          <input
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={inputClass}
            data-testid="login-password-input"
          />
        </Field>
        <SubmitButton busy={busy} testId="login-submit-button">
          Sign in
        </SubmitButton>
      </form>
      <div className="mt-6 space-y-3 text-sm">
        <p className="body-text" data-testid="password-help-note">
          Forgotten your password? Contact your AnyTribe administrator and they will send you a link
          to set a new one.
        </p>
        <Link to="/" className="inline-block body-text hover:text-[#111111]">
          Back to website
        </Link>
      </div>
    </AuthShell>
  );
}
